import { PlasticsCalendarEventType } from '../enums/plastics-calendar-event-type.enum';
import { RotoForecastInput, RotoForecastResult } from '../models/plastics-agent-api.model';
import { PlasticsCalendarEvent } from '../models/plastics-calendar-event.model';
import {
    timeStrippedDateISOToPlasticsCalendarEventsDict,
    workingDaysToCalendarDaysAccountingForEvents,
} from './working-days-to-calendar-days.function';

export function calculateRotoScheduleForecast(input: RotoForecastInput): RotoForecastResult {
    const asOf = new Date(input.asOf);
    if (Number.isNaN(asOf.getTime())) throw new Error('asOf must be a valid ISO date.');

    const machines = new Map(input.machines.map(machine => [machine.id, machine]));
    const tasks = new Map(input.tasks.map(task => [task.id, task]));
    const calendarEvents: PlasticsCalendarEvent[] = input.calendarEvents.map((event, index) => ({
        id: `agent-api-${index}`,
        eventDate: new Date(event.eventDate),
        eventTimeSet: false,
        title: '',
        type: event.type as PlasticsCalendarEventType,
        notes: '',
    })).filter(event => !Number.isNaN(event.eventDate.getTime()));
    const eventsByDate = timeStrippedDateISOToPlasticsCalendarEventsDict(calendarEvents);

    const occurrences = new Map<string, number>();
    for (const list of input.productLists) {
        for (const taskId of list.moldingTaskIds) occurrences.set(taskId, (occurrences.get(taskId) ?? 0) + 1);
    }
    const duplicateTaskIds = [...occurrences.entries()].filter(([, count]) => count > 1).map(([id]) => id).sort();
    const staleTaskIds = [...occurrences.keys()].filter(id => {
        const task = tasks.get(id);
        return !task || task.status === 'complete' || task.status === 'canceled';
    }).sort();
    const activeTasks = input.tasks.filter(task => task.status !== 'complete' && task.status !== 'canceled');
    const unscheduledTaskIds = activeTasks.filter(task => !occurrences.has(task.id)).map(task => task.id).sort();
    const emitted = new Set<string>();
    const results: RotoForecastResult['tasks'] = [];
    const warnings: string[] = [];

    if (duplicateTaskIds.length) warnings.push(`${duplicateTaskIds.length} molding task(s) appear more than once on the schedule.`);
    if (staleTaskIds.length) warnings.push(`${staleTaskIds.length} stale or missing molding task reference(s) were excluded.`);
    if (unscheduledTaskIds.length) warnings.push(`${unscheduledTaskIds.length} active molding task(s) are not scheduled.`);

    for (const list of input.productLists) {
        const machine = machines.get(list.machineId);
        if (!machine || !Number.isFinite(machine.approximateDailyCycleCount) || machine.approximateDailyCycleCount <= 0) {
            warnings.push(`Schedule list ${list.id} has no machine with a usable daily cycle rate.`);
            continue;
        }
        if (!machine.armIds.includes(list.armId)) warnings.push(`Schedule list ${list.id} references an unknown machine arm.`);

        let cumulativeCalendarDays = 0;
        for (let queueIndex = 0; queueIndex < list.moldingTaskIds.length; queueIndex++) {
            const taskId = list.moldingTaskIds[queueIndex];
            if (emitted.has(taskId)) continue;
            const task = tasks.get(taskId);
            if (!task || task.status === 'complete' || task.status === 'canceled') continue;
            emitted.add(taskId);
            const remainingQuantity = Math.max(0, task.totalQuantity - task.completedQuantity);
            const workingDays = remainingQuantity / machine.approximateDailyCycleCount;
            cumulativeCalendarDays += workingDaysToCalendarDaysAccountingForEvents(
                workingDays,
                cumulativeCalendarDays,
                eventsByDate,
                asOf,
            );
            const completionDate = new Date(asOf);
            completionDate.setDate(completionDate.getDate() + cumulativeCalendarDays);
            results.push({
                moldingTaskId: task.id,
                productId: task.productId,
                orderedProductId: task.orderedProductId,
                machineId: machine.id,
                machineName: machine.name,
                armId: list.armId,
                queueIndex,
                remainingQuantity,
                workingDays,
                cumulativeCalendarDays,
                estimatedCompletionDate: completionDate.toISOString(),
            });
        }
    }

    return { tasks: results, unscheduledTaskIds, duplicateTaskIds, staleTaskIds, warnings: [...new Set(warnings)] };
}
